import type { AxiosResponse } from 'axios'
import { useAuthStore } from '../stores/authStore'
import { i18n } from '../i18n'
import toast from 'react-hot-toast'

/**
 * Response success handler
 */
export function handleResponseSuccess(response: AxiosResponse): AxiosResponse {
  return response
}

/**
 * Response error handler
 */
export function handleResponseError(error: any): Promise<never> {
  const { response } = error

  if (response?.status === 401) {
    // Unauthorized - clear auth only, don't redirect here
    // Let the component handle the redirect to avoid page refresh
    useAuthStore.getState().clearAuth()
    
    // Show error message from backend if available
    if (response?.data?.error?.message) {
      toast.error(response.data.error.message)
    } else if (response?.data?.error) {
      // Handle structured error
      handleApiError(response.data.error)
    }
  } else if (response?.status === 403) {
    // Show error message from backend if available
    // Only show if it's a structured error with a message
    if (response?.data?.error?.message) {
      toast.error(response.data.error.message)
    } else if (!response?.data?.error) {
      // Only show generic message if there's no structured error
      toast.error('Access denied')
    }
  } else if (response?.status === 404) {
    // Show error message from backend if available
    // Only show if it's a structured error with a message
    if (response?.data?.error?.message) {
      toast.error(response.data.error.message)
    } else if (!response?.data?.error) {
      // Only show generic message if there's no structured error
      toast.error('Resource not found')
    }
  } else if (response?.status >= 500) {
    // Show error message from backend if available
    if (response?.data?.error?.message) {
      toast.error(response.data.error.message)
    } else {
      toast.error('Server error. Please try again later.')
    }
  } else if (response?.data?.error) {
    // Handle validation errors and other structured errors
    handleApiError(response.data.error)
  } else {
    // Only show generic error if we don't have a specific error message from backend
    // Filter out axios generic error messages and don't show for handled status codes
    const errorMessage = error?.message
    if (errorMessage && 
        !errorMessage.includes('Request failed with status code') && 
        !errorMessage.includes('Network Error') &&
        response?.status !== 400 &&
        response?.status !== 401 &&
        response?.status !== 403 &&
        response?.status !== 404 &&
        !(response?.status >= 500)) {
      toast.error(errorMessage)
    }
  }

  return Promise.reject(error)
}

/**
 * Function to handle API errors with validation support
 */
function handleApiError(error: any) {
  // Check if it's a validation error
  if (error.message) {
    // Handle general error message
    toast.error(error.message)
  } else {
    // Fallback
    toast.error(i18n.t('serverError'))
  }
}
